import { useCallback, useState } from 'react';
import axios from 'axios';
import { AppointmentBooking, AvailableTimeWindow } from '../types/appointment';

const getBackendApiRoot = (): string => {
  return (
    process.env.GATSBY_BACKEND_API_ROOT || process.env.BACKEND_API_ROOT || ''
  );
};

const isApiRootConfigured = (): boolean => {
  const apiRoot = getBackendApiRoot();
  return apiRoot.length > 0;
};

export const useAppointment = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchAvailableTimeWindows = useCallback(async (): Promise<
    AvailableTimeWindow[]
  > => {
    setLoading(true);
    setError(null);

    try {
      if (!isApiRootConfigured()) {
        throw new Error(
          'BACKEND_API_ROOT environment variable is not set. Please configure your backend API URL.'
        );
      }

      const apiRoot = getBackendApiRoot();
      const response = await axios.get(`${apiRoot}/api/slots`);
      return response.data;
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : 'Failed to fetch available appointment slots';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  const bookAppointment = useCallback(
    async (appointmentData: AppointmentBooking): Promise<void> => {
      setLoading(true);
      setError(null);

      try {
        if (!isApiRootConfigured()) {
          throw new Error(
            'BACKEND_API_ROOT environment variable is not set. Please configure your backend API URL.'
          );
        }

        const apiRoot = getBackendApiRoot();
        await axios.post(`${apiRoot}/api/appointments`, appointmentData);
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to book appointment';
        setError(errorMessage);
        throw new Error(errorMessage);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    loading,
    error,
    fetchAvailableTimeWindows,
    bookAppointment,
    clearError,
  };
};
